import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';

export interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  selectedOptions: Option[];
  title: string;
  setSelectedOptions: React.Dispatch<React.SetStateAction<Option[]>>;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, selectedOptions, title, setSelectedOptions }) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on the search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle selecting/unselecting options
  const handleOptionToggle = (option: Option) => {
    if (selectedOptions.find((o) => o.value === option.value)) {
      // Remove the option if it's already selected
      setSelectedOptions(selectedOptions.filter((o) => o.value !== option.value));
    } else {
      // Add the option if it's not selected
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="mb-1 block text-sm font-medium text-gray-700">{title}</div>
      <div className="mt-1">
        <div
          className="flex w-full flex-wrap items-center rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span
                key={option.value}
                className="mr-2 inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-700"
              >
                {option.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionToggle(option);
                  }}
                  className="ml-1 text-indigo-500 hover:text-indigo-600 focus:outline-none"
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span className="text-gray-500">Select...</span>
          )}
          <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
        </div>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg border border-gray-300">
            <input
              type="text"
              placeholder="Search..."
              className="w-full py-2 px-3 border-b border-gray-300 focus:outline-none"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <label key={option.value} className="flex items-center px-3 py-2 hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={!!selectedOptions.find((o) => o.value === option.value)}
                      onChange={() => handleOptionToggle(option)}
                      className="mt-1 w-5 h-5 mr-2 rounded-md border border-gray-300"
                    />
                    {option.label}
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
